import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../../shared/core/discriminated-union-read.schema";
import { VibeBuildJobSchema, VibeDeploymentSchema } from "./deployments.schemas";

// ----------------------------------------------------------------------------
// Cancel a deployment's build
// ----------------------------------------------------------------------------

export const CancelVibeDeploymentBuildPathVarsSchema = z.object({
  appId: z.string().uuid(),
  deploymentId: z.string().uuid()
});

/**
 * The two answers a cancel can get, discriminated on `outcome` so a caller
 * cannot read a cancellation that did not happen.
 *
 * - `cancelled` — THIS call stopped the build: the build job is now CANCELLED
 *   and the deployment CANCELLED. The tenant agent is signalled on commit and
 *   stops the executor build on its next pass.
 * - `already_ended` — there was nothing to stop, so nothing was written: the
 *   build had already ended (succeeded, failed, was superseded, or was cancelled
 *   by an earlier request) or the deployment had already left BUILDING. The
 *   rows are returned as they stand, so the caller shows what actually became of
 *   the build rather than a cancellation that did not happen.
 *
 * Both are a 2xx. `already_ended` is not an error: a double click, a CLI retry
 * and a build that finished a second before the click all land here, and each
 * wants the build's real outcome, not a refusal.
 */
export const CancelVibeDeploymentBuildResponseSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("cancelled"),
    deployment: VibeDeploymentSchema,
    buildJob: VibeBuildJobSchema
  }),
  z.object({
    outcome: z.literal("already_ended"),
    deployment: VibeDeploymentSchema,
    buildJob: VibeBuildJobSchema.nullable()
  })
]);

export type CancelVibeDeploymentBuildResponse = z.infer<
  typeof CancelVibeDeploymentBuildResponseSchema
>;

/**
 * The cancel response as a READER parses it: an outcome a newer backend added
 * reads as the server's own word instead of failing a cancel that already ran.
 */
export const CancelVibeDeploymentBuildResponseReadSchema = z.union([
  CancelVibeDeploymentBuildResponseSchema,
  z.looseObject({ outcome: unlistedDiscriminantSchema(CancelVibeDeploymentBuildResponseSchema) })
]);
